import { TCardState } from "../../../../@types";

export type PrefetchStore<T> = {
  prefetch: (state?: TCardState) => Promise<T>;
  consume: (state?: TCardState) => Promise<T | undefined>;
  reset: (state?: TCardState) => void;
};

export function createPrefetchStore<T>(
  fetcher: (state?: TCardState) => Promise<T>
): PrefetchStore<T> {
  const pendingByState = new Map<TCardState, Promise<T>>();
  const cachedByState = new Map<TCardState, T>();

  // AIDEV-NOTE: Shared promise cache keyed by card state to reuse iframe payloads across async boundaries
  async function prefetch(state: TCardState = "open"): Promise<T> {
    if (cachedByState.has(state)) {
      return cachedByState.get(state)!;
    }

    if (!pendingByState.has(state)) {
      const request = fetcher(state)
        .then((value) => {
          cachedByState.set(state, value);
          pendingByState.delete(state);
          return value;
        })
        .catch((error) => {
          pendingByState.delete(state);
          throw error;
        });

      pendingByState.set(state, request);
    }

    return pendingByState.get(state)!;
  }

  async function consume(state: TCardState = "open"): Promise<T | undefined> {
    if (cachedByState.has(state)) {
      return cachedByState.get(state);
    }

    const pending = pendingByState.get(state);
    if (!pending) {
      return undefined;
    }

    try {
      const value = await pending;
      cachedByState.set(state, value);
      return value;
    } catch {
      pendingByState.delete(state);
      return undefined;
    }
  }

  function reset(state?: TCardState) {
    if (state) {
      pendingByState.delete(state);
      cachedByState.delete(state);
      return;
    }

    pendingByState.clear();
    cachedByState.clear();
  }

  return { prefetch, consume, reset };
}

export const helpers = { createPrefetchStore };
